import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Icon, ListScreen as RdsListScreen } from "@/deps/ui-library";
import {
  DataTable,
  TableSkeleton,
  type Column,
  type EmptyStateCopy,
} from "@/components/list/DataTable";
import { ListFilterBar, type SelectOption } from "@/components/list/ListFilterBar";
import { listPaging } from "@/components/list/ListFooter";
import {
  clearFilters,
  fiscalYearValue,
  hasActiveFilters,
  toListQuery,
  type ListSearch,
} from "@/components/list/listQuery";
import { useCalendar, useFiscalYears } from "@/api/queries";
import { monthLabel, monthsOf } from "@/lib/calendar";

export const DOCUMENT_STATUSES: SelectOption[] = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "verified", label: "Verified" },
  { value: "approved", label: "Approved" },
];

/** Every route that renders a list. The union keeps `useSearch({ from })` typed. */
export type ListRoute =
  | "/sales/invoices"
  | "/sales/quotations"
  | "/sales/credit-notes"
  | "/sales/receipts"
  | "/purchases/orders"
  | "/purchases/bills"
  | "/purchases/payments"
  | "/purchases/debit-notes"
  | "/banking/transfers"
  | "/accounting/journals";

/**
 * What every list screen here shares, on RDS `ListScreen`: the URL's filters and paging, and the
 * fiscal years and months the filters offer.
 *
 * Sales, purchases and banking differ only in their columns and their words. Writing the frame
 * five times is how five screens end up paging differently, and how one of them forgets to reset
 * to page 1 when a filter changes.
 */
export function ListScreen<Row>({
  route,
  title,
  subtitle,
  actions,
  columns,
  rows,
  total,
  loading,
  error,
  onRetry,
  rowKey,
  rowLabel,
  onRowClick,
  searchPlaceholder,
  statuses = DOCUMENT_STATUSES,
  summary,
  payments,
  empty,
  filteredEmpty,
}: {
  route: ListRoute;
  title: string;
  subtitle: string;
  actions?: ReactNode;
  columns: Array<Column<Row>>;
  rows: Row[];
  total: number;
  /** Whether the API has a page after this one. The table pages by `total`; kept for the screens. */
  hasNext: boolean;
  loading: boolean;
  /** The request failed. Without this a failure would read as "No invoices yet". */
  error?: unknown;
  onRetry?: () => void;
  rowKey: (row: Row) => string;
  rowLabel: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  searchPlaceholder: string;
  /** `null` for a document with no approval ladder: the filter is not offered. */
  statuses?: SelectOption[] | null;
  /** Figures between the header and the list. */
  summary?: ReactNode;
  payments?: SelectOption[];
  empty: EmptyStateCopy;
  filteredEmpty: Omit<EmptyStateCopy, "action">;
}) {
  const search = useSearch({ from: route }) as ListSearch;
  const navigate = useNavigate();
  const setSearch = (next: ListSearch) => void navigate({ to: route, search: next, replace: true });
  const { data: years = [] } = useFiscalYears();
  const calendar = useCalendar();
  const fiscalYears: SelectOption[] = years.map((year) => ({
    value: fiscalYearValue(year),
    label: year.label,
  }));
  // The months of the year in the filter, or of the current one.
  const shown =
    years.find((year) => fiscalYearValue(year) === search.fy) ??
    years.find((year) => year.isCurrent);
  const months: SelectOption[] = (shown ? monthsOf(shown, calendar) : []).map((value) => ({
    value,
    label: monthLabel(value),
  }));

  return (
    // The shell's main has the padding.
    <RdsListScreen
      className="p-0"
      title={title}
      description={subtitle}
      actions={actions}
      filters={
        <>
          {summary}
          <ListFilterBar
            search={search}
            onChange={setSearch}
            searchPlaceholder={searchPlaceholder}
            statuses={statuses ?? undefined}
            payments={payments}
            fiscalYears={fiscalYears}
            months={months}
          />
        </>
      }
      loading={loading}
      loadingContent={<TableSkeleton columns={columns.length} />}
      error={error}
      onRetry={onRetry}
      empty={rows.length === 0}
      filtered={hasActiveFilters(search)}
      onClearFilters={() => setSearch(clearFilters(search))}
      emptyState={{
        title: empty.title,
        description: empty.description,
        icon: <Icon name={empty.icon} size="lg" color="inherit" />,
        action: empty.action,
      }}
      filteredEmptyState={{
        title: filteredEmpty.title,
        description: filteredEmpty.description,
      }}
    >
      <DataTable
        caption={title}
        columns={columns}
        rows={rows}
        rowKey={rowKey}
        rowLabel={rowLabel}
        onRowClick={onRowClick}
        paging={listPaging(search, setSearch, total)}
      />
    </RdsListScreen>
  );
}

/** Turns the URL's list state into the query the API takes, with cursor paging underneath. */
export function listQueryFor(search: ListSearch) {
  const page = search.page ?? 1;
  const size = search.size ?? 25;
  return {
    ...toListQuery(search),
    cursor: page > 1 ? String((page - 1) * size) : undefined,
  };
}
